/**
 * Reads the rendered `<meta name="robots">` tag from the document and parses
 * the directive tokens. Used by the Segment client beacon to attach
 * SEO-compliance properties to every `page()` event so that violations
 * (e.g. a `/ppc/...` page returning `index, follow`) become observable
 * in Mixpanel.
 *
 * SSR-safe: returns `null` values when `document` is unavailable so it can be
 * called from React effects without guards.
 */

type RenderedRobots = {
  /**
   * `true` when the page does NOT have a `noindex` directive (i.e. the page
   * is permitted to be indexed). `null` when no robots meta tag is present
   * or the document is unavailable (SSR).
   */
  index: boolean | null;
  /**
   * `true` when the page does NOT have a `nofollow` directive. `null` when
   * no robots meta tag is present or the document is unavailable (SSR).
   */
  follow: boolean | null;
  /**
   * Raw `content` attribute value of the robots meta tag, lowercased.
   * Useful for debugging which exact directive was rendered (e.g.
   * `"noindex, nofollow, noarchive"`). `null` when the tag is absent.
   */
  raw: string | null;
};

function parseRobotsContent(
  content: string | null | undefined,
): RenderedRobots {
  if (!content) {
    return { follow: null, index: null, raw: null };
  }

  const normalized = content.toLowerCase();
  const tokens = new Set(
    normalized
      .split(",")
      .map((token) => token.trim())
      .filter(Boolean),
  );

  return {
    follow: !tokens.has("nofollow") && !tokens.has("none"),
    index: !tokens.has("noindex") && !tokens.has("none"),
    raw: normalized,
  };
}

function getRenderedRobots(): RenderedRobots {
  if (typeof document === "undefined") {
    return { follow: null, index: null, raw: null };
  }

  const meta = document.querySelector<HTMLMetaElement>('meta[name="robots" i]');

  return parseRobotsContent(meta?.content);
}

export { getRenderedRobots, parseRobotsContent };
